// Cloud shadows: the sun's beam through the cloud volume, on everything the light falls on (the ground, the plants, the rocks, the sea).
//
// The volume is the cloud as the model has it (cloudVolume.ts), and the shadow of a cloud is the model's own: for a point on the ground, the optical depth on the ray to the sun through that volume.
// The volume's baked light is no use for it (the server bakes the beam only inside cloud, where the light is wanted), so this marches the same density the picture is drawn from, with the same
// choice of level (which level a point is read from depends on its distance from the eye: the finest ring that holds it and is near enough, blended into the next), the same unit and the same
// exponent on the density, along the ray to the beam's body, with the planet curving away under it. A mile of cloud between here and the sun is a mile of cloud in the shadow.
//
// A map, not a march in every shader: the transmittance is drawn into nested top-down rings round the eye (a clipmap like the ground's: RINGS windows of N x N texels, each twice the one before,
// 2 km across to 131 km, so a texel is 16 m at your feet and a kilometre at the horizon), at the height of the ground under each texel. A ring is drawn again when its window has to move (the eye
// is no longer in the middle of it), when its picture is old (the finest every 150 ms, the coarsest every 1.2 s) or when the beam has moved; at most two rings are drawn in a frame. A window sits on
// a lattice of its own texel, in the world's frame (not the eye's), so a texel is the same piece of ground whenever it is drawn and nothing shimmers as you move.
// The cost is a draw of N x N texels of a loop of at most 192 steps, a few times a second, and one texture lookup (two: the eye's own value as well) in each lit fragment.
//
// What a lit fragment does with it (csmShadows.ts patches three's light chunks, and only the materials it sets up): the beam is scaled by the map's transmittance at the fragment over the transmittance
// the beam already has in it. The beam that reaches the lights (DefaultSimRenderer: skyLight's, taken through the volume at the eye) has the eye's own transmittance on the ray to the sun in it
// (skyLight.beam, passed to `update`), so the ratio is what the place is to the eye: flying above the cloud the beam is whole and the cloud's shadows fall on the ground below it; under the cloud
// the beam is dim and a patch in the sun a mile off gets the beam it would have with no cloud (the ratio is limited to 25). The sky's light, the ambient, is the eye's, scaled the same way by
// the cloud overhead (the map's second channel: the optical depth straight up, a share 1 / (1 + 0.11 tau) of the sky's light passes) over what the eye has, and the part of the beam the cloud scatters down
// (the map's third channel, the optical depth on the beam, through a two-stream share) is added where it is more than the eye's own: the shade of a thick cloud is lit by the cloud, not black.
//
// Not in it: the sun's disc and the glare (the sky's), rain's own extinction, the fine detail the picture adds below a cell (a shadow's edge is as sharp as the model's cell), the shadow of one thing
// on another (the cascades' business), and the cloud's own light (the volume has it).
import * as THREE from 'three';
import { LEVEL_UNIFORMS_GLSL, LEVEL_FUNCS_GLSL, type CloudVolume } from './cloudVolume';
import { GROUND_GLSL, type GroundClip } from './groundClip';
import { planet } from './planet';
import { origin } from './origin';

/** The share of the sky's light that a cloud of optical depth `tau` straight overhead leaves: a thick cloud passes about 1 / (1 + 0.11 tau) of it, and it hides at most half of the dome (a cloud is a blob, not the whole sky: the rest
 *  of the dome is open round it). The shader has the same formula (`cloudSunT`). */
export function cloudSkyShare(tau: number): number { return 1 - 0.5 * (1 - 1 / (1 + 0.11 * Math.max(tau, 0))); }

/** The share of a beam (down to level ground) that a cloud with the optical depth `tauPath` on the beam's ray, at the sine of elevation `mu`, scatters down instead of passing or stopping: what a thick cloud passes in all,
 *  1 / (1 + 0.11 tau mu) with the vertical optical depth, less what it passes unscattered. The shader has the same formula. */
export function cloudDiffuseShare(tauPath: number, mu: number): number { return Math.max(1 / (1 + 0.11 * tauPath * mu) - Math.exp(-tauPath), 0); }

export const CS_RINGS = 7;
export const CS_N = 128;
export const CS_W0 = 2048;                       // the finest window's edge (m): ring k is CS_W0 x 2^k across (a power of two, so a texel divides the origin's lattice, origin.ts)
const PERIOD_MS = [150, 200, 250, 350, 500, 800, 1200];
const MAX_PER_FRAME = 2;
const MIN_ELEV = 0.0087;                         // the beam is not cast under half a degree: its ray would not leave the ground's own air
const STEP_MAX = 192;
const SUN_MOVED = 0.002;                         // radians of the beam's direction (a tenth of a degree) before every ring is redrawn

// ---- the pass -----------------------------------------------------------------------------------------------------------------------------------------------------

const PASS_VERT = /* glsl */`
void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }`;

const PASS_FRAG = /* glsl */`
precision highp float; precision highp sampler3D; precision highp sampler2DArray;
uniform vec4 uPlanet;
${LEVEL_UNIFORMS_GLSL}
${GROUND_GLSL}
uniform vec4 uWin;          // this ring: the corner x, z of its window (counted from O), a texel (m)
uniform vec3 uSun;          // the unit vector to the beam's body (x east, y up, z south)
uniform vec3 uEye;          // the eye counted from O
uniform float uSunOn;
uniform float uStepK;       // the share of a step's length it grows by per metre of path (a quality knob)
uniform float uSteep;       // the exponent the picture puts on the density share (cloudVolume.ts STEEP_DEFAULT): the march's own uniform
${LEVEL_FUNCS_GLSL}
float hash12(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
// the share of the extinction's unit at a point, as the march blends it (the levels that count there, each with the exponent the picture puts on the density)
float shareHere(vec3 pw, float dist) {
  int k0, k1, k2; vec3 ws;
  int n = ringSet(pw, dist, k0, k1, k2, ws);
  if (n == 0) return 0.0;
  float d0 = readD(k0, pw);
  float x = ws.x * (d0 > 0.0 ? pow(d0 * d0, uSteep) : 0.0);
  if (n > 1) { float d1 = readD(k1, pw); x += ws.y * (d1 > 0.0 ? pow(d1 * d1, uSteep) : 0.0); }
  if (n > 2) { float d2 = readD(k2, pw); x += ws.z * (d2 > 0.0 ? pow(d2 * d2, uSteep) : 0.0); }
  return x;
}
// how far along a path whose height has the slope sl and curves up by cc s^2 it has risen by dz (1e9: never)
float riseLen(float dz, float sl, float cc) {
  if (dz <= 0.0) return 0.0;
  float den = sl + sqrt(sl * sl + 4.0 * cc * dz);
  return den > 1.0e-6 ? 2.0 * dz / den : 1.0e9;
}
void main() {
  vec2 g = uWin.xy + gl_FragCoord.xy * uWin.w;              // the middle of this texel, counted from O
  float T = 1.0, TZ = 0.0, TS = 0.0;
  if (uSunOn > 0.5 && uAny > 0.5) {
    float hg = max(groundYRel(g), 0.0);                      // the ground's height (the sea's is 0)
    float inv2R = uPlanet.x > 0.0 ? 0.5 / uPlanet.x : 0.0;
    vec2 ge = g - uEye.xz;
    // the beam's ray from the ground, in the frame of the picture's own march (a point's height is its y lifted by the horizontal distance from the eye squared over 2R): the beam is a straight
    // line in the flat frame, so a point at a distance from the eye sees the beam tilted by that over R
    float sl = max(uSun.y + 2.0 * inv2R * dot(ge, uSun.xz), 0.01);
    float cc = inv2R * dot(uSun.xz, uSun.xz);
    float sEnd = min(4.0e5, riseLen(uSpan - hg, sl, cc));
    float tau = 0.0, s = 0.0;
    for (int i = 0; i < ${STEP_MAX}; i++) {
      float ds = clamp(uStepK * s, 60.0, 500.0);
      if (i == 0) s = hash12(floor(g / uWin.w) + uWin.w) * ds;
      if (s > sEnd || tau > 25.0) break;
      vec3 pw = vec3(g.x + uSun.x * s, hg + sl * s + cc * s * s, g.y + uSun.z * s);
      float x = shareHere(pw, distance(pw, uEye));
      tau += x * uExtMax * ds;
      s += ds;
    }
    T = exp(-tau); TS = tau;
    // and straight up: the cloud's optical depth overhead, for the sky's light (a thick cloud passes about 1 / (1 + 0.11 tau) of it, scattered)
    float sEndZ = max(uSpan - hg, 0.0), s2 = 0.0;
    for (int i = 0; i < 96; i++) {
      float ds = clamp(uStepK * 3.0 * s2, 80.0, 800.0);
      if (i == 0) s2 = hash12(floor(g / uWin.w) * 1.7 + uWin.w + 17.0) * ds;
      if (s2 > sEndZ || TZ > 60.0) break;
      vec3 pw = vec3(g.x, hg + s2, g.y);
      TZ += shareHere(pw, distance(pw, uEye)) * uExtMax * ds;
      s2 += ds;
    }
  }
  gl_FragColor = vec4(T, min(TZ, 200.0), TS, 1.0);
}`;

// ---- what a lit fragment reads --------------------------------------------------------------------------------------------------------------------------------------

/** Declarations and the lookup, put at the top of three's lights_pars_begin chunk (under USE_CLOUD_SHADOW: a material that has not been set up does not have them). */
export const CLOUD_SHADOW_PARS_GLSL = /* glsl */`
#ifdef USE_CLOUD_SHADOW
uniform highp sampler2DArray uCsMap;
uniform vec4 uCsWin[${CS_RINGS}];     // per ring: the window's corner x, z (counted from O), 1 / its edge, 1 once it is drawn
uniform vec3 uCsEye;                  // the eye counted from O
uniform float uCsOn;
uniform vec4 uCsPar;                  // the transmittance the beam has in it already (the eye's), the sine of the beam's elevation, the share of the sky's light the cloud overhead leaves at the eye (cloudSkyShare)
uniform vec4 uCsFill;                 // the beam's horizontal irradiance with no cloud in the lights' units (a colour), and the share of it the cloud on the eye's ray has scattered down (cloudDiffuseShare): already in the eye's light
float cloudSkyShare(float tau) { return 1.0 - 0.5 * (1.0 - 1.0 / (1.0 + 0.11 * max(tau, 0.0))); }                                      // (the same as the function in cloudShadow.ts)
float cloudDiffuseShare(float tauPath, float mu) { return max(1.0 / (1.0 + 0.11 * tauPath * mu) - exp(-tauPath), 0.0); }
float gCloudSunT = 1.0;
float gCloudAmb = 1.0;
vec3 gCloudFill = vec3(0.0);
// the map at p (counted from O): r the transmittance on the beam, g the optical depth straight up, b the optical depth on the beam; the finest ring that holds it, blended into the next over the outer part of its window
vec3 csRead(vec2 p) {
  for (int k = 0; k < ${CS_RINGS}; k++) {
    vec4 w = uCsWin[k];
    if (w.w < 0.5) continue;
    vec2 u = (p - w.xy) * w.z;
    float e = min(min(u.x, u.y), min(1.0 - u.x, 1.0 - u.y));
    if (e > 0.02) {
      vec3 a = texture(uCsMap, vec3(u, float(k))).rgb;
      if (k < ${CS_RINGS - 1} && e < 0.14) {
        vec4 w2 = uCsWin[k + 1];
        if (w2.w > 0.5) {
          vec2 u2 = (p - w2.xy) * w2.z;
          a = mix(texture(uCsMap, vec3(clamp(u2, 0.0, 1.0), float(k + 1))).rgb, a, smoothstep(0.02, 0.14, e));
        }
      }
      return a;
    }
  }
  vec4 wc = uCsWin[${CS_RINGS - 1}];
  if (wc.w < 0.5) return vec3(1.0, 0.0, 0.0);
  return texture(uCsMap, vec3(clamp((p - wc.xy) * wc.z, 0.0, 1.0), ${(CS_RINGS - 1).toFixed(1)})).rgb;
}
// what the cloud does to the light at the point whose view-space position is viewPos (what the fragment sees), against what it does at the eye (what the lights already have in them):
// the beam times the transmittance there over the one it has; the sky's light times the share the cloud overhead passes there over the eye's; and, where the beam is blocked more than at the eye,
// the part of it the cloud scatters down (the same share, of the beam's irradiance), so a place in the shade of a thick cloud is lit by the cloud and not by the clear sky alone
void cloudSunT(vec3 viewPos) {
  if (uCsOn < 0.5) return;
  vec3 dW = transpose(mat3(viewMatrix)) * viewPos;
  vec3 m = csRead(uCsEye.xz + dW.xz);
  gCloudSunT = clamp(m.x / max(uCsPar.x, 0.04), 0.0, 25.0);
  gCloudAmb = clamp(cloudSkyShare(m.y) / max(uCsPar.z, 0.3), 0.0, 4.0);
  gCloudFill = uCsFill.rgb * max(cloudDiffuseShare(m.z, uCsPar.y) - uCsFill.a, 0.0);
}
#endif
`;

/** Put the shadow in three's light chunks. Idempotent. Call after the CSM has put its own chunks in (its constructor replaces them). Returns false (and changes nothing) if a chunk is not as expected. */
export function installCloudShadowChunks(): boolean {
  const C = THREE.ShaderChunk as unknown as Record<string, string>;
  let pars = C.lights_pars_begin!, begin = C.lights_fragment_begin!;
  if (pars.includes('gCloudSunT') && begin.includes('gCloudSunT')) return true;
  const a = /light\.color = directionalLight\.color;/;
  const b = /vec3 geometryPosition = - vViewPosition;/;
  if (!a.test(pars) || !b.test(begin)) { console.warn('[cloud shadow] three\'s light chunks are not as expected: no cloud shadows'); return false; }
  pars = CLOUD_SHADOW_PARS_GLSL + pars.replace(a, 'light.color = directionalLight.color;\n#ifdef USE_CLOUD_SHADOW\nlight.color *= gCloudSunT;\n#endif');
  begin = begin.replace(b, 'vec3 geometryPosition = - vViewPosition;\n#ifdef USE_CLOUD_SHADOW\ncloudSunT( geometryPosition );\n#endif');
  const c = '#if defined( RE_IndirectSpecular )';
  if (begin.split(c).length !== 2) { console.warn('[cloud shadow] three\'s light chunks are not as expected: no cloud shadows'); return false; }
  begin = begin.replace(c, '#if defined( RE_IndirectDiffuse ) && defined( USE_CLOUD_SHADOW )\nirradiance = irradiance * gCloudAmb + gCloudFill;\n#endif\n' + c);
  C.lights_pars_begin = pars; C.lights_fragment_begin = begin;
  return true;
}

// ---- the rings ------------------------------------------------------------------------------------------------------------------------------------------------

interface Ring {
  ax: number; az: number;     // the window's corner in the world's frame (m): counted from O it is the uniform
  drawnMs: number;            // when it was drawn (performance.now, ms); -1e9 for never
  sun: THREE.Vector3;         // the beam it was drawn for
}

/** The uniforms every set-up lit material shares (csmShadows.ts adds them to its program). Made before the shadow itself, so a material set up early has them (the shadow is off until it fills them in). */
export function makeCloudShadowUniforms(): Record<string, THREE.IUniform> {
  const win: THREE.Vector4[] = [];
  for (let k = 0; k < CS_RINGS; k++) win.push(new THREE.Vector4(0, 0, 1, 0));
  return { uCsMap: { value: null }, uCsWin: { value: win }, uCsEye: { value: new THREE.Vector3() }, uCsOn: { value: 0 }, uCsPar: { value: new THREE.Vector4(1, 1, 1, 0) }, uCsFill: { value: new THREE.Vector4() } };
}

export class CloudShadow {
  /** The uniforms every set-up lit material shares (see makeCloudShadowUniforms). */
  readonly uniforms: Record<string, THREE.IUniform>;
  private readonly rt: THREE.WebGLArrayRenderTarget;
  private readonly mat: THREE.ShaderMaterial;
  private readonly scene = new THREE.Scene();
  private readonly cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly rings: Ring[] = [];
  private readonly win: THREE.Vector4[];
  private readonly eye: THREE.Vector3;
  private enabled = true;
  private stepK = 0.03;
  /** Rings drawn in the last update, and in all (for an overlay or a test). */
  drawnLast = 0; drawnTotal = 0;
  /** The transmittance the map has at the eye is the shaders'; the last frame's state, for the overlay. */
  on = false;

  /** `shared`: the uniforms the materials were given (makeCloudShadowUniforms; the shadow fills them in), or none to make its own. */
  constructor(private cloud: CloudVolume, ground: GroundClip, shared?: Record<string, THREE.IUniform>) {
    this.rt = new THREE.WebGLArrayRenderTarget(CS_N, CS_N, CS_RINGS, {
      format: THREE.RGBAFormat, type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false,
      wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping,
    } as any);
    this.rt.texture.generateMipmaps = false;
    for (let k = 0; k < CS_RINGS; k++) this.rings.push({ ax: 0, az: 0, drawnMs: -1e9, sun: new THREE.Vector3() });
    this.uniforms = shared ?? makeCloudShadowUniforms();
    this.uniforms.uCsMap!.value = this.rt.texture;
    this.win = this.uniforms.uCsWin!.value as THREE.Vector4[];
    this.eye = this.uniforms.uCsEye!.value as THREE.Vector3;
    for (const w of this.win) w.w = 0;
    this.mat = new THREE.ShaderMaterial({
      vertexShader: PASS_VERT, fragmentShader: PASS_FRAG, depthTest: false, depthWrite: false, toneMapped: false, fog: false,
      uniforms: {
        ...cloud.shadowUniforms(), ...ground.uniforms, ...planet.uniforms,
        uWin: { value: new THREE.Vector4() }, uSun: { value: new THREE.Vector3(0, 1, 0) }, uEye: { value: this.eye }, uSunOn: { value: 1 }, uStepK: { value: this.stepK },
      },
    });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.mat);
    quad.frustumCulled = false;
    this.scene.add(quad);
  }

  setEnabled(on: boolean) { this.enabled = on; if (!on) { this.uniforms.uCsOn!.value = 0; this.on = false; } }
  get isEnabled() { return this.enabled; }
  /** The march's growth of its step with the path (a share of the path; 0.03 default): smaller is finer and dearer. */
  setStepK(k: number) { this.stepK = Math.max(0.005, Math.min(0.2, k)); this.mat.uniforms.uStepK!.value = this.stepK; this.invalidate(); }
  /** The ring window's size (m) and the texel of ring k. */
  static windowM(k: number): number { return CS_W0 * (1 << k); }
  static texelM(k: number): number { return CS_W0 * (1 << k) / CS_N; }
  /** Forget every ring (the cloud or the ground came in a new form): they are drawn again, two a frame. */
  invalidate() { for (const r of this.rings) r.drawnMs = -1e9; }

  /** Once a frame, after the camera and the origin are set and before the scene is drawn. `beam`: the unit vector to the body whose light casts (the sun's, or the moon's when it leads), `beamOn`: that one is up
   *  and the picture is the planet's (not from space), `nowMs`: performance.now, `eyeT`: the transmittance on the beam at the eye that the lights' colour already has in it (1 if none), `eyeTauUp`: the cloud's optical
   *  depth straight up from the eye (skyLight.tauUp; 0 if none), `fill`: the beam's horizontal irradiance at the eye in the lights' units (its colour x its intensity x the sine of its
   *  elevation, the transmittance at the eye in it). */
  update(renderer: THREE.WebGLRenderer, camera: THREE.Camera, beam: THREE.Vector3, beamOn: boolean, nowMs: number, eyeT = 1, eyeTauUp = 0, fill?: ArrayLike<number>): void {
    this.drawnLast = 0;
    const U = this.uniforms;
    if (!this.enabled || !beamOn || beam.y < MIN_ELEV || !this.cloud.shadowReady) { U.uCsOn!.value = 0; this.on = false; return; }
    origin.relTo(camera.position.x, camera.position.y, camera.position.z, this.eye);
    const ox = origin.org.x, oz = origin.org.y;
    // the windows are kept in the world's frame; counted from O they are the uniform (O may have moved since)
    const rel = (ax: number, az: number, out: THREE.Vector4, k: number) => out.set(ax - ox, az - oz, 1 / CloudShadow.windowM(k), out.w);
    const ex = this.eye.x + ox, ez = this.eye.z + oz;                 // the eye in the world's frame (doubles)
    // which rings want drawing: the eye left the middle half of the window, the picture is old, or the beam has turned
    const due: Array<{ k: number; urgency: number }> = [];
    for (let k = 0; k < CS_RINGS; k++) {
      const r = this.rings[k]!, W = CloudShadow.windowM(k);
      let urgency = 0;
      if (r.drawnMs < -1e8) urgency = 1e6 - k;
      else {
        const off = Math.max(Math.abs(ex - (r.ax + 0.5 * W)), Math.abs(ez - (r.az + 0.5 * W)));
        if (off > 0.25 * W) urgency = 1e5 + off / W;
        else if (r.sun.angleTo(beam) > SUN_MOVED) urgency = 1e3;
        else { const age = nowMs - r.drawnMs; if (age > PERIOD_MS[k]!) urgency = age / PERIOD_MS[k]!; }
      }
      if (urgency > 0) due.push({ k, urgency });
    }
    due.sort((a, b) => b.urgency - a.urgency);
    const prevRT = renderer.getRenderTarget();
    const prevAuto = renderer.autoClear;
    let drawn = 0;
    if (due.length) {
      renderer.autoClear = false;
      const P = this.mat.uniforms;
      (P.uSun!.value as THREE.Vector3).copy(beam);
      for (const d of due) {
        if (drawn >= MAX_PER_FRAME) break;
        const k = d.k, r = this.rings[k]!, W = CloudShadow.windowM(k), tx = CloudShadow.texelM(k);
        // the window is centred on the eye, on its lattice (a multiple of four texels, so two neighbouring rings' lattices nest)
        const q = 4 * tx;
        r.ax = Math.round(ex / q) * q - 0.5 * W; r.az = Math.round(ez / q) * q - 0.5 * W;
        (P.uWin!.value as THREE.Vector4).set(r.ax - ox, r.az - oz, 0, tx);
        // (uWin.xy is the corner counted from O; the shader adds the texel's middle)
        renderer.setRenderTarget(this.rt, k);
        renderer.render(this.scene, this.cam);
        r.drawnMs = nowMs; r.sun.copy(beam);
        this.win[k]!.w = 1;
        drawn++;
      }
      renderer.setRenderTarget(prevRT);
      renderer.autoClear = prevAuto;
    }
    for (let k = 0; k < CS_RINGS; k++) rel(this.rings[k]!.ax, this.rings[k]!.az, this.win[k]!, k);
    this.drawnLast = drawn; this.drawnTotal += drawn;
    const par = U.uCsPar!.value as THREE.Vector4;
    par.x = eyeT; par.y = beam.y; par.z = cloudSkyShare(eyeTauUp);
    const k = 1 / Math.max(eyeT, 0.04);                                // (the beam with no cloud in it)
    (U.uCsFill!.value as THREE.Vector4).set((fill?.[0] ?? 0) * k, (fill?.[1] ?? 0) * k, (fill?.[2] ?? 0) * k, cloudDiffuseShare(-Math.log(Math.max(eyeT, 1e-4)), beam.y));
    U.uCsOn!.value = 1; this.on = true;
  }

  dispose() { this.rt.dispose(); this.mat.dispose(); this.scene.children.forEach(c => (c as THREE.Mesh).geometry?.dispose()); }

  describe(): string {
    const ages = this.rings.map(r => (r.drawnMs < -1e8 ? '-' : ''));
    return `cloud shadows ${this.enabled ? (this.on ? 'on' : 'idle') : 'off'}: ${CS_RINGS} rings of ${CS_N}^2 (${CloudShadow.texelM(0)} m to ${CloudShadow.texelM(CS_RINGS - 1)} m a texel), ${this.drawnLast} drawn last frame, ${this.drawnTotal} in all${ages.some(a => a) ? ', some not drawn yet' : ''}`;
  }
}
